import { useEffect, useState } from 'react';
import { ApolloCache } from '@apollo/client';
import gql from 'graphql-tag';

const SLOT_FRAGMENT = gql`
  fragment SalesOrderSlot on Sink {
    __typename
    id
    externalId
    timelineUsage {
      id
      startTime
      endTime
      resourceId
    }
  }
`;

export type SalesOrderSlot = {
  __typename: string;
  id: ID;
  externalId?: string | null;
  timelineUsage: {
    id: ID;
    startTime: Date;
    endTime: Date;
    resourceId: ID;
  } | null;
};

const pending = new Set<string>();
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((listener) => listener());
}

export function markSalesOrderWritePending(itemId: ID) {
  pending.add(itemId);
  notify();
}

export function clearSalesOrderWritePending(itemId: ID) {
  pending.delete(itemId);
  notify();
}

export function useSalesOrderWritePending(itemId: ID): boolean {
  const [, setVersion] = useState(0);
  useEffect(() => {
    const onChange = () => setVersion((n) => n + 1);
    listeners.add(onChange);
    return () => {
      listeners.delete(onChange);
    };
  }, []);
  return pending.has(itemId);
}

export function readItemSlot(
  cache: ApolloCache<unknown>,
  itemId: ID,
): SalesOrderSlot | null {
  return (
    cache.readFragment<SalesOrderSlot>({
      id: `Sink:${itemId}`,
      fragment: SLOT_FRAGMENT,
      fragmentName: 'SalesOrderSlot',
    }) ?? null
  );
}

export function writeItemSlot(
  cache: ApolloCache<unknown>,
  item: SalesOrderSlot,
  startTime: Date,
) {
  if (item.timelineUsage == null) {
    throw new Error(`Item ${item.id} has no timeline position`);
  }

  cache.writeFragment({
    id: `Sink:${item.id}`,
    fragment: SLOT_FRAGMENT,
    fragmentName: 'SalesOrderSlot',
    data: {
      ...item,
      timelineUsage: {
        ...item.timelineUsage,
        __typename: 'TimelineUsage',
        startTime: startTime.toISOString(),
        endTime: startTime.toISOString(),
      },
    },
  });
}

export async function withSalesOrderOptimisticMove(
  cache: ApolloCache<unknown>,
  item: SalesOrderSlot,
  startTime: Date,
  persist: () => Promise<boolean>,
): Promise<boolean> {
  if (item.timelineUsage == null) {
    throw new Error(`Sales-order sink ${item.id} has no timeline position`);
  }

  const previous = item.timelineUsage.startTime;
  writeItemSlot(cache, item, startTime);
  markSalesOrderWritePending(item.id);
  try {
    const ok = await persist();
    if (!ok) {
      writeItemSlot(cache, item, previous);
    }
    return ok;
  } catch (error) {
    writeItemSlot(cache, item, previous);
    throw error;
  } finally {
    clearSalesOrderWritePending(item.id);
  }
}
